/**
 * Servicios relacionados con negocios
 */

import { apiGet } from './api';
import { aplicarIconosApp } from '../utils/iconosApp';

/**
 * Obtiene negocios por tipo (comida, tienda, envio)
 * @param {string} type - Tipo de negocio
 * @returns {Promise<Array>} - Lista de negocios
 */
export const getBusinessesByType = async (type) => {
  const data = await apiGet(`/api/businesses/type/${type}`);
  return data.response || [];
};

/**
 * Obtiene todos los negocios
 * @returns {Promise<Array>} - Lista de todos los negocios
 */
export const getAllBusinesses = async () => {
  const data = await apiGet('/api/businesses/businesses');
  return data.response || data.businesses || [];
};

/**
 * Single-restaurant: obtiene EL restaurante (Business isDefault:true).
 * @returns {Promise<Object|null>} - El restaurante único (con location, address, etc.)
 */
export const getDefaultBusiness = async () => {
  const data = await apiGet('/api/businesses/default');
  return data.business || data.response || null;
};

// Copia local del restaurante (stale-while-revalidate): la app arranca con la
// última versión guardada y se actualiza cuando llega la nueva. Así, desde la
// segunda apertura, el home se pinta al instante aunque la red tarde.
const RESTAURANTE_KEY = 'restaurante_v1';

export const leerRestauranteGuardado = () => {
  try { return JSON.parse(localStorage.getItem(RESTAURANTE_KEY)) || null; } catch { return null; }
};

// El restaurante es único y prácticamente estático → memoizamos la promesa
// para que todos los consumidores (ETA, header, splash…) compartan un solo
// fetch. Cada respuesta buena reemplaza la copia local.
let _defaultBusinessPromise = null;
export const getDefaultBusinessCached = () => {
  if (!_defaultBusinessPromise) {
    _defaultBusinessPromise = getDefaultBusiness()
      .then((b) => {
        try { if (b) localStorage.setItem(RESTAURANTE_KEY, JSON.stringify(b)); } catch { /* sin storage */ }
        aplicarIconosApp(b);
        return b;
      })
      .catch((e) => {
        _defaultBusinessPromise = null; // permitir reintento si falló
        throw e;
      });
  }
  return _defaultBusinessPromise;
};

/**
 * Obtiene las categorías de negocios por tipo
 * @param {string} type - Tipo de negocio
 * @returns {Promise<Array>} - Lista de categorías
 */
export const getBusinessCategories = async (type) => {
  const data = await apiGet(`/api/business-categories/type/${type}`);
  return data.categories || data.response || [];
};

/**
 * Obtiene todos los productos
 * @returns {Promise<Array>} - Lista de productos
 */
export const getAllProducts = async () => {
  const data = await apiGet('/api/products');
  return data.products || data.response || [];
};

/**
 * Obtiene productos populares agrupados por negocio
 * @returns {Promise<Object>} - { [businessId]: Product[] }
 */
export const getPopularProducts = async () => {
  const data = await apiGet('/api/products/popular');
  const products = data.products || data.response || [];
  const grouped = {};
  products.forEach(product => {
    const bizId = product.businessId?._id || product.businessId;
    if (!bizId) return;
    if (!grouped[bizId]) grouped[bizId] = [];
    if (grouped[bizId].length < 6) {
      grouped[bizId].push({ ...product, isPopular: true });
    }
  });
  return grouped;
};

/**
 * Obtiene productos de portada (curados por el dueño) agrupados por negocio.
 * Tope de 10 por negocio (coincide con el límite del backend).
 * @returns {Promise<Object>} - { [businessId]: Product[] }
 */
export const getFeaturedProducts = async () => {
  const data = await apiGet('/api/products/featured');
  const products = data.products || data.response || [];
  const grouped = {};
  products.forEach(product => {
    const bizId = product.businessId?._id || product.businessId;
    if (!bizId) return;
    if (!grouped[bizId]) grouped[bizId] = [];
    if (grouped[bizId].length < 10) {
      grouped[bizId].push({ ...product, isFeatured: true });
    }
  });
  return grouped;
};

/**
 * Productos curados por el ADMIN para la sección "Por menos de $X" del home.
 * Selección manual desde el panel admin (NO por precio automático).
 * @returns {Promise<Product[]>} array plano (businessId populado), orden por creación
 */
